import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import PasswordInput from '../../components/common/PasswordInput';
import '../../assets/styles/auth.css';

/**
 * Serves two audiences with the same link shape (`/reset-password?token=...`):
 * a newly invited team member setting their first password, and an existing
 * user resetting a forgotten one. The wording is deliberately neutral so it
 * reads correctly either way.
 */
const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const getPasswordStrength = () => {
    if (!password) return { strength: 0, text: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;

    const texts = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    return { strength, text: texts[strength] };
  };

  const passwordStrength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password set. You can sign in now.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.error || 'We could not set your password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-form-section">
          <Link to="/" className="auth-logo">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork<span className="brand-360">360</span></span>
          </Link>

          {!token ? (
            <>
              <div className="auth-header">
                <h1>This link is not valid</h1>
                <p>
                  The link is missing its security token, or it has already been used. Request a new
                  one and we&rsquo;ll email it to you.
                </p>
              </div>

              <div className="auth-form">
                <Link to="/forgot-password" className="btn-auth-primary">
                  <i className="fas fa-paper-plane"></i>
                  Request a new link
                </Link>
              </div>
            </>
          ) : success ? (
            <>
              <div className="auth-alert success">
                <i className="fas fa-check-circle"></i>
                <div>
                  <strong>Password set</strong>
                  <p style={{ margin: '0.25rem 0 0' }}>
                    You can now sign in with your email and new password.
                  </p>
                </div>
              </div>

              <div className="auth-header">
                <h1>You&rsquo;re all set</h1>
                <p>Taking you to the sign in page...</p>
              </div>

              <div className="auth-form">
                <Link to="/login" className="btn-auth-primary">
                  <i className="fas fa-sign-in-alt"></i>
                  Continue to Sign In
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="auth-header">
                <h1>Choose your password</h1>
                <p>
                  Set a password for your SafeWork360 account. You&rsquo;ll use it with your email
                  address to sign in.
                </p>
              </div>

              {error && (
                <div className="auth-alert error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}

              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="password">New password</label>
                  <PasswordInput
                    leftIcon="fas fa-lock"
                    wrapperClassName="input-icon-wrapper"
                    className="form-control"
                    id="password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                  />
                  {password && (
                    <div className="password-strength">
                      <div className="strength-bar">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`strength-segment ${
                              passwordStrength.strength >= level
                                ? ['weak', 'fair', 'good', 'strong'][passwordStrength.strength - 1]
                                : ''
                            }`}
                          />
                        ))}
                      </div>
                      <span className="strength-text">{passwordStrength.text}</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  <PasswordInput
                    leftIcon="fas fa-lock"
                    wrapperClassName="input-icon-wrapper"
                    className="form-control"
                    id="confirmPassword"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" className="btn-auth-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-lock"></i>
                      Save password
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div className="auth-footer">
            Already know your password? <Link to="/login">Sign in</Link>
          </div>
        </div>

        {/* Features Section */}
        <div className="auth-features-section">
          <div className="features-content">
            <h2>Your account, your password</h2>
            <p>
              Nobody else can see the password you choose here, not even the administrator who
              invited you.
            </p>

            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-user-lock"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Private by design</h4>
                  <p>Passwords are hashed and never stored in readable form</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-hourglass-half"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Time-limited links</h4>
                  <p>Setup and reset links expire, so old emails cannot be reused</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
